import { observer } from 'mobx-react-lite';
import { FC, useEffect, useMemo, useState } from 'react';
import { Spacer, TextBody, TextTitle } from '@pypestream/design-system';

import { usePublishModal } from '../components';
import { useLoadingState, useProStudioViewerRole } from '../hooks';
import { tableStore } from '../stores/table-store';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../xstate/app.xstate';
import { PublishPanel, type DeployEnv } from './deploy/publish-panel';
import { PublishedVersions } from './deploy/published-versions';
import {
  diffSolutions,
  totalChanges,
  versionNumber,
} from './deploy/solution-diff';
import { asVersion } from './deploy/version-runs';
import { sendUserMessage } from '../xstate/app.xstate-utils';
import {
  useSolutionVersionId,
  useVersionRows,
} from './deploy/use-version-rows';
import {
  KnowledgeSearchNotice,
  useKnowledgeSearchBlocksPublish,
  useKnowledgeSearchPublishTooltip,
} from '../components/knowledge-search/knowledge-search-notice';

import './deploy-stage.scss';

const envLabel = (env: string) =>
  env.toLowerCase() === 'live' ? 'Live' : 'Sandbox';

/**
 * Project Bobcat, 5-stage nav — the Deploy stage.
 *
 * Left: the real Versions page body (solution name, environment cards) and an
 * expandable list of published versions. Right: the release panel — what
 * publishing the draft changes, node by node, and the Publish action, which
 * opens the same `PublishModal` the header's Publish uses.
 *
 * The draft is compared with the version already in the chosen environment
 * (that's what a publish there replaces); with nothing there yet, with the
 * latest published version; with nothing published at all, it's a first
 * publish.
 *
 * Sandbox first, then Live: Validate can only test what's published, and it
 * tests Sandbox. So the draft goes to Sandbox, is tested there, and Live
 * takes that same tested version — a republish of it, as Versions does —
 * rather than the draft, whose later edits nobody has tested. A solution with
 * no Sandbox, or nothing in it yet, publishes the draft to Live as before.
 */
export const DeployStage: FC = observer(() => {
  const { draftVersionId, versions, deployedVersions, envs, botType } =
    useProStudioCtxSelector((ctx) => ({
      draftVersionId: ctx.draftVersion,
      versions: ctx.versions,
      deployedVersions: ctx.deployedVersions,
      envs: ctx.envs ?? [],
      botType: ctx.selectedBot?.botType,
    }));
  const isViewer = useProStudioViewerRole();
  const isLoading = useLoadingState();
  const blockedByKnowledgeSearch = useKnowledgeSearchBlocksPublish();
  const knowledgeSearchTooltip = useKnowledgeSearchPublishTooltip();
  const [env, setEnv] = useState<DeployEnv>('sandbox');

  const {
    publishModal,
    openModal: openPublishModal,
    closeModal: closePublishModal,
  } = usePublishModal({
    draftVersionId: draftVersionId ?? undefined,
    initialEnv: env,
  });

  const { solutionVersionPublishingInProgress } = tableStore;

  // Same as PrimaryNav: the modal hands off to the in-progress state.
  useEffect(() => {
    if (solutionVersionPublishingInProgress) {
      closePublishModal();
    }
  }, [solutionVersionPublishingInProgress, closePublishModal]);

  const latestPublished = useMemo(
    () =>
      (versions ?? [])
        .map((v) => v.version)
        .filter((v): v is string => !!v)
        .sort((a, b) => versionNumber(b) - versionNumber(a))[0] ?? null,
    [versions]
  );
  const inEnv = deployedVersions?.[env] || null;
  // What Validate is testing: the version in Sandbox.
  const sandboxVersion = envs.includes('sandbox')
    ? deployedVersions?.sandbox || null
    : null;
  // Live takes the tested Sandbox version, not the draft.
  const promoteVersion = env === 'live' ? sandboxVersion : null;
  const alreadyLive =
    !!promoteVersion &&
    asVersion(promoteVersion) === asVersion(deployedVersions?.live);
  // Promoting compares with what's live (nothing yet: a first live publish).
  const baselineVersion = promoteVersion ? inEnv : (inEnv ?? latestPublished);
  const baselineEnvLabel = inEnv
    ? envLabel(env)
    : envLabel(
        (versions ?? []).find((v) => v.version === baselineVersion)?.env ?? env
      );

  // What the draft becomes when it's published. A v2 draft carries no number
  // of its own (`draft`), so it's the next one after the latest published.
  const draftNumber = versionNumber(draftVersionId ?? '');
  const nextVersion =
    draftNumber > 0
      ? (draftVersionId as string)
      : `v${(latestPublished ? versionNumber(latestPublished) : 0) + 1}`;

  const toId = useSolutionVersionId();
  const baseline = useVersionRows(toId(baselineVersion));
  const promoted = useVersionRows(toId(promoteVersion));

  // Read in render so the diff follows draft edits.
  const draftRows = [
    ...tableStore.getAllSortedRows(),
    ...tableStore.pendingRowsToRender,
  ];
  const outgoing = promoteVersion ? promoted.rows : draftRows;
  const diff =
    outgoing && (!baselineVersion || baseline.rows)
      ? diffSolutions(baseline.rows ?? [], outgoing)
      : null;
  // Draft edits the promoted version doesn't have: they need their own test.
  const draftAhead =
    promoteVersion && promoted.rows
      ? totalChanges(diffSolutions(promoted.rows, draftRows).counts)
      : 0;

  const promote = () => {
    const version = promoteVersion;
    if (!version || !botType) return;
    // Versions' Republish, to Live: once the mutation is accepted, progress
    // and outcome are announced by the router's publish watcher off the
    // deployment rows, as for every publish.
    sendProStudioEvent({
      type: 'publishBot',
      payload: {
        republish: true,
        env: 'live',
        version,
        botType,
        onPublishComplete: (result) => {
          if (result.status !== 'error') return;
          sendUserMessage({
            type: 'error',
            text: `Couldn’t put ${asVersion(version)} live. Try again, or republish it from its row in Deploy.`,
            autoClose: 5000,
          });
        },
      },
    });
  };

  // A knowledge base still being set up holds Publish, as in the header.
  const settingUpKnowledge =
    !blockedByKnowledgeSearch && tableStore.isWaitingForLiveCorpusIdOnly;
  const isPublishDisabled =
    isLoading ||
    blockedByKnowledgeSearch ||
    settingUpKnowledge ||
    !tableStore.canPublish ||
    tableStore.isValidationInProgress ||
    solutionVersionPublishingInProgress;
  const publishTooltip = solutionVersionPublishingInProgress
    ? 'Publishing is in progress.'
    : blockedByKnowledgeSearch
      ? knowledgeSearchTooltip
      : settingUpKnowledge
        ? "We're still setting things up. Publishing will be available soon."
        : tableStore.isValidationInProgress
          ? 'Validation is running.'
          : isPublishDisabled
            ? 'Fix any errors to enable publishing.'
            : undefined;

  return (
    <div className="c-deploy-stage" data-testid="bobcat-deploy-stage">
      <div className="c-deploy-stage__main">
        <TextTitle size="small">Deploy</TextTitle>
        <TextBody variant="secondary">
          Every version of the solution that has been published, where it runs
          and who published it. The draft waiting to go out is on the right.
        </TextBody>
        <Spacer size="large" />
        {!isViewer && <KnowledgeSearchNotice />}
        <PublishedVersions isViewer={isViewer} />
      </div>
      <PublishPanel
        draftVersion={nextVersion}
        baselineVersion={baselineVersion}
        baselineEnvLabel={baselineEnvLabel}
        diff={diff}
        diffError={baseline.status === 'error' ? baseline.error : null}
        env={env}
        onEnvChange={setEnv}
        envs={envs}
        canPublish={!isPublishDisabled}
        publishTooltip={publishTooltip}
        onPublish={() => {
          sendProStudioEvent({ type: 'loadPublishModalData' });
          openPublishModal();
        }}
        promoteVersion={promoteVersion ? asVersion(promoteVersion) : null}
        sandboxVersion={sandboxVersion}
        alreadyLive={alreadyLive}
        draftAhead={draftAhead}
        promoteError={promoted.status === 'error' ? promoted.error : null}
        onPromote={promote}
        isViewer={isViewer}
      />
      {publishModal}
    </div>
  );
});
